import { Box, Button, chakra, Heading, HStack, Tag, Text, VStack } from '@chakra-ui/react'
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime } from 'luxon'
import type { ReactElement } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'

import { useAppContext } from '@/client/context/app'
import { DIALOGUES_REFLECTION_EXPIRES_AFTER_DAYS } from '@/client/services/dialogues/constants'
import getSessionTimespanString from '@/client/services/series/getSessionTimespanString'

import RichText from '@/components/UI/RichTextEditor/RichText'
import { TooltipButton } from '@/components/UI/TooltipButton'
import Card from '@/components/Worlds/UI/Card'

import type { SeriesHomeScheduleSessionDetails } from './types'

function SessionTag({
  numberColorScheme,
  positionInSeries,
}: {
  numberColorScheme: 'purple' | 'gray'
  positionInSeries: number
}) {
  return (
    <Tag
      size="md"
      colorScheme={numberColorScheme}
      bg={numberColorScheme === 'gray' ? 'gray.muted' : undefined}
      whiteSpace="nowrap"
      minW={{ base: 'unset', md: '93px' }}
      py="4px"
      maxW="unset"
      w="fit-content"
    >
      SESSION {positionInSeries}
    </Tag>
  )
}

export default function SessionCard({
  session,
  isUserRegisteredLearner,
  isLastCard,
  openExcusedAbsenceModal,
  openReflectionModal,
  isDialogue,
}: {
  session: SeriesHomeScheduleSessionDetails
  isUserRegisteredLearner: boolean
  isLastCard: boolean
  openExcusedAbsenceModal: () => void
  openReflectionModal: () => Promise<void>
  isDialogue: boolean
}): ReactElement {
  const {
    isUpcoming = false,
    positionInSeries,
    startDate,
    endDate,
    title,
    topicName,
    description,
    attendancePolicy,
    didOrWillAttend,
    attemptedBreakout,
    reflections,
  } = session

  const numberColorScheme = isUpcoming ? 'purple' : 'gray'
  const dateOpacity = isUpcoming ? 1 : 0.5

  const hasDialoguesReflection = reflections.some(reflection => reflection.type === 'dialogues')
  const { requestTimestamp } = useAppContext()
  const now = DateTime.fromISO(requestTimestamp)

  return (
    <HStack alignItems="stretch">
      <VStack
        display={{ base: 'none', md: 'flex' }}
        width="75px"
        gap="8px"
        overflow="visible"
        alignItems="stretch"
        zIndex={1}
        backgroundColor="bg.subtle"
        pt="12px"
        pb="16px"
        alignSelf={!isLastCard ? 'flex-start' : 'unset'}
      >
        <SessionTag numberColorScheme={numberColorScheme} positionInSeries={positionInSeries} />
        <VStack alignItems="center" spacing="0" opacity={dateOpacity}>
          <Heading size="xl" color="gray.fg">
            {startDate.day}
          </Heading>
          <Text variant="eyebrow" color="gray.fg" suppressHydrationWarning>
            {startDate.toFormat('MMM')}
          </Text>
        </VStack>
      </VStack>
      <Card p="24px" flexGrow={1}>
        <VStack alignItems="stretch">
          <VStack spacing="2px" alignItems="stretch" flexGrow={1}>
            <HStack
              alignItems={{ base: 'center', md: 'flex-start' }}
              justifyContent="space-between"
              mb={{ base: '10px', md: 0 }}
            >
              <Box display={{ base: 'block', md: 'none' }}>
                <SessionTag
                  numberColorScheme={numberColorScheme}
                  positionInSeries={positionInSeries}
                />
              </Box>
              <Heading size="lg" display={{ base: 'none', md: 'block' }}>
                {title !== '' ? title : topicName}
              </Heading>
              <HStack spacing="8px">
                {isUpcoming ? (
                  <>
                    {attendancePolicy === 'required' ? (
                      !isUserRegisteredLearner || didOrWillAttend ? (
                        <Tag colorScheme="yellow">REQUIRED</Tag>
                      ) : isUserRegisteredLearner && !didOrWillAttend ? (
                        <Tag colorScheme="gray">EXCUSED</Tag>
                      ) : null
                    ) : null}
                    {attendancePolicy === 'required' && isUserRegisteredLearner ? (
                      <Button
                        variant="outline"
                        aria-label="Excused absence dropdown"
                        onClick={openExcusedAbsenceModal}
                        rightIcon={
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            color={chakraTokenToVar('fg.muted')}
                            size="xs"
                          />
                        }
                        color="fg.muted"
                        minW="unset"
                        height="unset"
                        fontSize="sm"
                        py="5px"
                        pl="12px"
                        pr="10px"
                      >{`${didOrWillAttend ? '' : 'Not '}Attending`}</Button>
                    ) : null}
                  </>
                ) : isUserRegisteredLearner && !didOrWillAttend ? (
                  <Text fontSize="sm" color="fg.subtle" fontWeight="semibold">
                    You missed this session
                  </Text>
                ) : isDialogue ? (
                  (() => {
                    const endedMoreThan7DaysAgo =
                      endDate.toMillis() <
                      now.minus({ days: DIALOGUES_REFLECTION_EXPIRES_AFTER_DAYS }).toMillis()
                    const disabledReason = !attemptedBreakout
                      ? 'You can only complete a reflection if you participated in a breakout session.'
                      : reflections.length === 0 && endedMoreThan7DaysAgo
                        ? `You can only add a reflection up to ${DIALOGUES_REFLECTION_EXPIRES_AFTER_DAYS} days after the session ends.`
                        : reflections.length > 0 && !hasDialoguesReflection
                          ? 'Reflection is not available for this session.'
                          : null
                    const isDisabled = disabledReason != null
                    const label = reflections.length > 0 ? 'Reflection' : 'Complete Reflection'
                    const variant = reflections.length > 0 ? 'outline' : 'solid'
                    const color = reflections.length > 0 ? 'fg.muted' : undefined
                    return (
                      <TooltipButton
                        tooltipContent={disabledReason}
                        onClick={() => openReflectionModal()}
                        isDisabled={isDisabled}
                        variant={variant}
                        color={color}
                        fontSize="sm"
                        py="6px"
                        px="12px"
                        shouldWrapChildren
                      >
                        {label}
                      </TooltipButton>
                    )
                  })()
                ) : null}
              </HStack>
            </HStack>
            <Heading size="lg" display={{ base: 'block', md: 'none' }}>
              {title !== '' ? title : topicName}
            </Heading>
            <Text
              color="fg.muted"
              fontWeight="semibold"
              textTransform="uppercase"
              suppressHydrationWarning
            >
              <chakra.span display={{ base: 'none', md: 'block' }}>
                {getSessionTimespanString({ startDate, endDate, dateFormat: 'day-of-week' })}
              </chakra.span>
              <chakra.span display={{ base: 'block', md: 'none' }}>
                {getSessionTimespanString({ startDate, endDate, dateFormat: 'day-of-month' })}
              </chakra.span>
            </Text>
          </VStack>
          <RichText content={description} />
        </VStack>
      </Card>
    </HStack>
  )
}
